
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { useState, useEffect } from "react";
import axios from "axios";
import { requestID, baseUrl, config } from "../../common/utils";
import { useBusinessContext } from "../../context/business-context";
import { mockServiceList } from "../../components/mock-service";


type InvoiceSuspenseItem = {
  id: number;
  invoice_id: string;
  customer_id: string;
  service_id: string;
  service_amount: string;
  raised_by: string;
  branch_id: string;
  business_id: string;
  status: string;
  created_at: string;
  updated_at: string;
};

type ServiceItem = {
  // Multiple possible field names for flexibility
  service_id?: string;
  id?: string;
  serviceId?: string;
  
  name?: string;
  service_name?: string;
  serviceName?: string;
  description?: string;
  
  amount?: string | number;
  price?: string | number;
  rate?: string | number;
  service_amount?: string | number;
  
  [key: string]: any; // Allow any other properties
};

type Props = {
  invoiceId: string;
  customerId?: string;
  serviceList?: ServiceItem[];
  showActions?: boolean;
  onSuspenseChange?: (total: number) => void;
  debug?: boolean; // Add debug mode
};


export default function InvoiceSuspense({
  invoiceId,
  customerId,
  serviceList = [],
  showActions = true,
  onSuspenseChange,
  debug = false, // Turn on to see debug info
}: Props) {
  const [suspenseList, setSuspenseList] = useState<InvoiceSuspenseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Add/Edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InvoiceSuspenseItem | null>(null);
  const [formData, setFormData] = useState({
    service_id: "",
    service_amount: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const { selectedBusinessId } = useBusinessContext();

  // Debug log
  useEffect(() => {
    if (debug) {
      console.log("InvoiceSuspense Debug:");
      console.log("Invoice ID:", invoiceId);
      console.log("Service List:", serviceList);
      console.log("Service List Length:", serviceList.length);
      console.log("First Service Item:", serviceList[0]);
      console.log("Available Service IDs:", 
        serviceList.map(s => ({
          id: s.service_id || s.id || s.serviceId,
          name: s.name || s.service_name || s.serviceName || s.description,
          amount: s.amount || s.price || s.rate || s.service_amount
        }))
      );
    }
  }, [serviceList, debug]);

  // Normalize service data to a consistent format
  const normalizeService = (service: ServiceItem) => {
    return {
      id: service.service_id || service.id || service.serviceId || "",
      name: service.name || service.service_name || service.serviceName || service.description || "Unknown Service",
      amount: service.amount || service.price || service.rate || service.service_amount || "0",
    };
  };

  const normalizedServices = serviceList.map(normalizeService);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceSuspense();
    }
  }, [invoiceId]);

  useEffect(() => {
    if (onSuspenseChange) {
      const total = suspenseList.reduce(
        (sum, item) => sum + parseFloat(item.service_amount),
        0
      );
      onSuspenseChange(total);
    }
  }, [suspenseList]);

  const fetchInvoiceSuspense = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("invoice_id", invoiceId);

      const response = await axios.post(
        baseUrl() + "invoicesuspenses/getInvoiceSuspense",
        formData,
        config
      );

      const data = await response.data;
      if (data.status) {
        setSuspenseList(data.data || []);
      } else {
        setError("Failed to load suspense records");
      }
    } catch (error) {
      console.error("Error fetching invoice suspense:", error);
      setError(
        error.response?.data?.message || "Failed to fetch suspense records"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingItem(null);
    setFormData({
      service_id: "",
      service_amount: "",
    });
    setModalOpen(true);
  };

  const handleOpenEditModal = (item: InvoiceSuspenseItem) => {
    if (item.status !== "pending") {
      setError("Only pending items can be edited");
      return;
    }
    
    setEditingItem(item);
    setFormData({
      service_id: item.service_id,
      service_amount: item.service_amount,
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFormData({
      service_id: "",
      service_amount: "",
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Auto-fill service amount if service is selected
    if (name === "service_id") {
      const selectedService = normalizedServices.find(
        (service) => service.id === value
      );
      if (selectedService && selectedService.amount) {
        setFormData((prev) => ({
          ...prev,
          service_amount: selectedService.amount.toString(),
        }));
      }
    }
  };

  const validateForm = () => {
    if (!formData.service_id) {
      setError("Please select a service");
      return false;
    }
    if (!formData.service_amount || parseFloat(formData.service_amount) <= 0) {
      setError("Please enter a valid service amount");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const formDataToSend = new FormData();
      formDataToSend.append("requestID", requestID());
      formDataToSend.append("invoice_id", invoiceId);
      formDataToSend.append("customer_id", customerId || "");
      formDataToSend.append("service_id", formData.service_id);
      formDataToSend.append("service_amount", formData.service_amount);

      const endpoint = editingItem
        ? "invoicesuspenses/updateInvoiceSuspense"
        : "invoicesuspenses/addInvoiceSuspense";

      if (editingItem) {
        formDataToSend.append("suspense_id", editingItem.id.toString());
      }

      const response = await axios.post(
        baseUrl() + endpoint,
        formDataToSend,
        config
      );

      const data = await response.data;

      if (data.status) {
        setSuccess(
          editingItem
            ? "Suspense item updated successfully"
            : "Suspense item added successfully"
        );
        handleCloseModal();
        fetchInvoiceSuspense();
      } else {
        setError(data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving invoice suspense:", error);
      setError(
        error.response?.data?.message || "Failed to save suspense item"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (suspenseId: number) => {
    if (!window.confirm("Are you sure you want to remove this suspense item?"))
      return;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("requestID", requestID());
      formDataToSend.append("suspense_id", suspenseId.toString());

      const response = await axios.post(
        baseUrl() + "invoicesuspenses/deleteInvoiceSuspense",
        formDataToSend,
        config
      );

      const data = await response.data;
      if (data.status) {
        setSuccess("Suspense item removed successfully");
        fetchInvoiceSuspense();
      } else {
        setError(data.message || "Failed to delete");
      }
    } catch (error) {
      console.error("Error removing suspense:", error);
      setError("Failed to remove suspense item");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const calculateTotal = () => {
    return suspenseList.reduce(
      (total, item) => total + parseFloat(item.service_amount),
      0
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning.main";
      case "approved":
        return "success.main";
      case "rejected":
        return "error.main";
      default:
        return "text.secondary";
    }
  };

  const getServiceName = (serviceId: string) => {
    const service = normalizedServices.find(s => s.id === serviceId);
    return service ? service.name : serviceId;
  };

  if (loading && suspenseList.length === 0) {
    return (
      <Card>
        <CardContent sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6">
                Invoice Suspense
                <Typography component="span" sx={{ ml: 1, fontSize: "0.875rem", color: "text.secondary" }}>
                  ({suspenseList.length} items)
                </Typography>
              </Typography>
              {debug && (
                <Tooltip title={`${normalizedServices.length} services available`}>
                  <InfoIcon color="info" fontSize="small" />
                </Tooltip>
              )}
            </Box>
            {showActions && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenAddModal}
                disabled={normalizedServices.length === 0}
              >
                Add Suspense
              </Button>
            )}
          </Box>
        }
      />

      <CardContent>
        {/* Debug Info */}
        {debug && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Debug Info:</Typography>
            <Typography variant="body2">
              Services: {normalizedServices.length} available<br />
              Invoice: {invoiceId}<br />
              First service ID: {normalizedServices[0]?.id || 'None'}
            </Typography>
          </Alert>
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {normalizedServices.length === 0 && showActions && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No services available. Please add services first.
          </Alert>
        )}

        {/* Suspense List */}
        {suspenseList.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">
              No suspense items found
            </Typography>
            {showActions && normalizedServices.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenAddModal}
                sx={{ mt: 2 }}
              >
                Add First Item
              </Button>
            )}
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    {showActions && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suspenseList.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        {getServiceName(item.service_id)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "medium" }}>
                        ${parseFloat(item.service_amount).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            color: getStatusColor(item.status),
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                          }}
                        >
                          {item.status}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(item.created_at)}
                        </Typography>
                      </TableCell>
                      {showActions && (
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditModal(item)}
                            disabled={item.status !== "pending"}
                            title={item.status !== "pending" ? "Only pending items can be edited" : "Edit"}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(item.id)}
                            disabled={item.status !== "pending"}
                            title={item.status !== "pending" ? "Only pending items can be deleted" : "Delete"}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Total Summary */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1" fontWeight="medium">
                Total Suspense Amount:
              </Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                ${calculateTotal().toFixed(2)}
              </Typography>
            </Box>
          </>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {editingItem ? "Edit Suspense Item" : "Add New Suspense Item"}
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Service *</InputLabel>
                <Select
                  name="service_id"
                  value={formData.service_id}
                  label="Service *"
                  onChange={handleSelectChange}
                  disabled={submitting || normalizedServices.length === 0}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <em>Select Service</em>;
                    }
                    const service = normalizedServices.find(s => s.id === selected);
                    return service ? service.name : selected;
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select Service</em>
                  </MenuItem>
                  {normalizedServices.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      <Box>
                        <Typography>{service.name}</Typography>
                        {service.amount && (
                          <Typography variant="caption" color="text.secondary">
                            ${typeof service.amount === 'string' ? parseFloat(service.amount).toFixed(2) : service.amount}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {normalizedServices.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    No services available. Please add services first.
                  </Typography>
                )}
              </FormControl>

              <TextField
                fullWidth
                name="service_amount"
                label="Amount *"
                type="number"
                value={formData.service_amount}
                onChange={handleInputChange}
                disabled={submitting}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  inputProps: { 
                    min: 0,
                    step: 0.01
                  }
                }}
                helperText="Enter the service amount"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseModal} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !formData.service_id || !formData.service_amount}
            >
              {submitting ? (
                <CircularProgress size={24} />
              ) : editingItem ? (
                "Update"
              ) : (
                "Add"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}